import React, { memo, useMemo } from "react";
import dayjs from "dayjs";
import { useHistory, useParams } from "react-router-dom";
import {
  CardNotification,
  Heading,
  Text,
  ThemeStore,
  Weekly,
  Link,
  Button,
  Tooltip,
  Pill,
} from "@foris/avocado-suite";
import cx from "classnames";
import { IParams } from "@models/IParams";
import { ClassroomBooking } from "@models/ISchema";
import { useTranslation } from "react-i18next";
import useBookingAccess from "@common/hooks/useBookingAccess";
import { getDateNumber } from "@utils/formats";
import css from "./detail.module.scss";

interface Props {
  booking: ClassroomBooking;
  intervals: any[];
}

const Resources: React.FC<Props> = ({ booking, intervals }) => {
  const history = useHistory();
  const { origin: originId, scenario: scenarioId, workspace: workspaceId }: IParams = useParams();
  const searchClassroomUrl = `${workspaceId}/${scenarioId}/${originId}/${booking?.id}`;
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const { isAdminBooking } = useBookingAccess();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const showMediaLinks = useMemo(() => {
    return (
      ["NSGR", "AAUD", "BAUD", "BSGR"].includes(booking?.classroom?.code) &&
      ["rejected", "pending", "confirmed"].includes(booking?.status)
    );
  }, [booking?.status, booking?.classroom?.code]);

  const resumeDetails = useMemo(() => {
    // If classroom is present, means that the booking is old type single classroom booking
    if (!!booking?.classroom) {
      return {
        campus: booking?.classroom?.building?.campus?.name ?? "-",
        building: booking?.classroom?.building?.name ?? "-",
        classroom: booking?.classroom?.name ?? "-",
        classroomType: booking?.classroom?.classroomType?.name ?? "-",
      };
    }

    const classrooms: Record<string, boolean> = {};
    const campuses: Record<string, boolean> = {};
    const buildings: Record<string, boolean> = {};
    const classroomTypes: Record<string, boolean> = {};

    booking?.sessions?.forEach(session => {
      session?.classrooms?.forEach(classroom => {
        if (classroom?.name || classroom?.code) {
          const nameOrCode = classroom?.name ?? classroom?.code;
          classrooms[nameOrCode] = true;
        }

        if (classroom?.building?.campus) {
          const nameOrCode = classroom?.building?.campus?.name ?? classroom?.building?.campus?.code;
          campuses[nameOrCode] = true;
        }

        if (classroom?.building) {
          const nameOrCode = classroom?.building?.name ?? classroom?.building?.code;
          buildings[nameOrCode] = true;
        }

        if (classroom?.classroomType) {
          const nameOrCode = classroom?.classroomType?.name ?? classroom?.classroomType?.code;
          classroomTypes[nameOrCode] = true;
        }
      });
    });

    return {
      campus: Object.keys(campuses)?.join(", ") ?? "-",
      building: Object.keys(buildings)?.join(", ") ?? "-",
      classroom: Object.keys(classrooms)?.join(", ") ?? "-",
      classroomType: Object.keys(classroomTypes)?.join(", ") ?? "-",
    };
  }, [booking]);

  const datesByClassroom = useMemo(() => {
    const dateToWeekDay = (date: string) => {
      const dayIdToWeekday = {
        [0]: t("day-list.short-names.sunday"),
        [1]: t("day-list.short-names.monday"),
        [2]: t("day-list.short-names.tuesday"),
        [3]: t("day-list.short-names.wednesday"),
        [4]: t("day-list.short-names.thursday"),
        [5]: t("day-list.short-names.friday"),
        [6]: t("day-list.short-names.saturday"),
      };
      return dayIdToWeekday[dayjs(date).day()];
    };

    return booking?.sessions
      ?.map(({ classrooms, scheduleEvent }) => {
        return classrooms?.map(classroom => {
          const event = scheduleEvent?.[0];
          // start and end are in the format "YYYY-MM-DD HH:MM:SS"
          // the time format is varibale based on the client data
          const [startDate, startTime] = event?.start.split(" ");
          const [, endTime] = event?.end.split(" ");
          // format start and end time to always be in "HH:MM" format
          const [startHour, startMinute] = startTime.split(":");
          const [endHour, endMinute] = endTime.split(":");
          const adaptedStartTime = `${getDateNumber(startHour || 0)}:${getDateNumber(
            startMinute || 0,
          )}`;
          const adaptedEndTime = `${getDateNumber(endHour || 0)}:${getDateNumber(endMinute || 0)}`;

          return {
            date: `${dateToWeekDay(startDate)} - ${dayjs(startDate).format("DD.MM.YYYY")}`,
            time: `${adaptedStartTime} - ${adaptedEndTime}`,
            classroom: classroom?.name,
            isSpecial: !!classroom?.isSpecialTagValue,
          };
        });
      })
      .flat();
  }, [booking, language]);

  return (
    <div className={css.detail}>
      <header className={css.detail_header}>
        <div className={css.headerTitle}>
          <Heading className={css.headerTitle_text} type="h3">
            {t("booking.booking-details.schedule-resume.title")}
          </Heading>
        </div>
      </header>

      <section className={css.detail_resume}>
        <div className={css.resumeItem}>
          <Text className={css.resumeItem_label}>
            {t("booking.booking-details.schedule-resume.campus")}
          </Text>
          <Text className={css.resumeItem_value}>{resumeDetails?.campus}</Text>
        </div>

        {/*
         * Only show the classroom for bookings with only one date
         */}
        {!datesByClassroom?.length && (
          <div className={css.resumeItem}>
            <Text className={css.resumeItem_label}>
              {t("booking.booking-details.schedule-resume.classroom")}
            </Text>
            <Text className={css.resumeItem_value}>{resumeDetails?.classroom}</Text>
          </div>
        )}

        <div className={css.resumeItem}>
          <Text className={css.resumeItem_label}>
            {t("booking.booking-details.schedule-resume.building")}
          </Text>
          <Text className={css.resumeItem_value}>{resumeDetails?.building}</Text>
        </div>

        <div className={css.resumeItem}>
          <Text className={css.resumeItem_label}>
            {t("booking.booking-details.schedule-resume.classroom-type")}
          </Text>
          <Text className={css.resumeItem_value}>{resumeDetails?.classroomType}</Text>
        </div>

        <div className={css.resumeItem}>
          <Text className={css.resumeItem_label}>
            {t("booking.booking-details.schedule-resume.capacity")}
          </Text>
          <Text className={css.resumeItem_value}>{booking?.capacity ?? "-"}</Text>
        </div>
      </section>

      <section className={css.detail_resume}>
        <div className={css.resumeItem}>
          <Text className={css.resumeItem_label}>
            {t("booking.booking-details.schedule-resume.booking-type")}
          </Text>
          <Text className={css.resumeItem_value}>
            {booking?.isRecurrent
              ? t("booking.booking-form.type-selector.options.recurring")
              : t("booking.booking-form.type-selector.options.single")}
          </Text>
        </div>
      </section>

      <section className={css.detail_resume}>
        <ul className={css.resumeSessions}>
          <li className={cx(css.resumeSession_item, css.resumeSession_header)}>
            <Text className={css.resumeSession_label}>
              {t("booking.booking-details.schedule-resume.day")}
            </Text>
            <Text className={css.resumeSession_label}>
              {t("booking.booking-details.schedule-resume.time")}
            </Text>
            <Text className={css.resumeSession_label}>
              {t("booking.booking-details.schedule-resume.classroom")}
            </Text>
          </li>
          {datesByClassroom?.map(({ date, time, classroom, isSpecial }) => (
            <li key={`${date}-${time}-${classroom}`} className={css.resumeSession_item}>
              <Text className={css.resumeSession_date}>{date}</Text>
              <Text className={css.resumeSession_time}>{time}</Text>
              <Text className={css.resumeSession_classroom}>
                {classroom}

                {isSpecial && (
                  <Tooltip label={t("booking.classroom-tags.special-tooltip")} placement="topLeft">
                    <Pill
                      icon="alert-triangle"
                      iconColor="warning-60"
                      textColor="warning-60"
                      color="warning-opacity"
                    >
                      {t("booking.classroom-tags.special")}
                    </Pill>
                  </Tooltip>
                )}
              </Text>
            </li>
          ))}
        </ul>
      </section>

      {!!booking?.isRecurrent && (
        <section className={css.detail_weekly}>
          <Weekly
            className={cx("editionWeekly", themeMode === "dark" && "editionWeekly__dark")}
            weeklyItems={intervals}
          />
        </section>
      )}

      {showMediaLinks && (
        <section className={cx(css.mediaLinks, themeMode === "dark" && css.mediaLinks__dark)}>
          <div className={css.mediaLinks_info}>
            <Text className={css.infoLabel} type="sm">
              {t("booking.booking-details.schedule-resume.media-resources.title")}
            </Text>

            <Link
              className={css.infoLink}
              href="https://bit.ly/3bOphsu"
              target="_blank"
              rel="noreferrer"
              iconRight="external-link"
            >
              {t("booking.booking-details.schedule-resume.media-resources.link-tec-resources")}
            </Link>
            <Link
              className={css.infoLink}
              href="https://bit.ly/3EPfgaL"
              target="_blank"
              rel="noreferrer"
              iconRight="external-link"
            >
              {t(
                "booking.booking-details.schedule-resume.media-resources.link-incidenes-resources",
              )}
            </Link>
          </div>

          {booking?.status === "rejected" && (
            <CardNotification
              title={t(
                "booking.booking-details.schedule-resume.media-resources.notification.title",
              )}
              state="info"
              className={cx(css.infoBox)}
            >
              {t(
                "booking.booking-details.schedule-resume.media-resources.notification.description",
              )}
            </CardNotification>
          )}
        </section>
      )}

      {isAdminBooking && (
        <Button
          className={css.detail_editBtn}
          variant="secondary"
          onClick={() => history.push(`/booking/search/${searchClassroomUrl}`)}
        >
          {t("booking.booking-details.actions.edit-booking")}
        </Button>
      )}
    </div>
  );
};

export default memo(Resources);
